import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Person,
  Settings,
  Security,
  ExitToApp,
  Dashboard,
  CreditCard
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const UserMenu: React.FC = () => {
  const { user, signOut, redirectToPartnerSSO } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();
    await signOut();
    navigate('/');
  };

  const handleMenuItemClick = (path: string) => {
    handleClose();
    navigate(path);
  };

  const handlePartnerSSO = async () => {
    handleClose();
    try {
      await redirectToPartnerSSO();
    } catch (error) {
      console.error('SSO redirect failed:', error);
    }
  };

  if (!user) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  //Debug
  console.log('User object:', user);
  console.log('User name:', user.name);
  console.log('Initials:', getInitials(user.name));

  return (
    <Box>
      <Button
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'white',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'secondary.main',
            fontSize: '0.875rem'
          }}
        >
          {getInitials(user.name)}
        </Avatar>
        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {user.name}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {user.emailVerified && (
              <Chip label="Verified" size="small" color="success" />
            )}
            {user.mfaEnabled && (
              <Chip label="2FA" size="small" color="primary" />
            )}
          </Box>
        </Box>

        {/* Menu Items */}
        <MenuItem onClick={() => handleMenuItemClick('/dashboard')}>
          <ListItemIcon>
            <Dashboard fontSize="small" />
          </ListItemIcon>
          Dashboard
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick('/profile')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick('/subscription')}>
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          Subscription
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick('/settings')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick('/security')}>
          <ListItemIcon>
            <Security fontSize="small" />
          </ListItemIcon>
          Security
        </MenuItem>

        <Divider />

        <MenuItem onClick={handlePartnerSSO}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          Access Partner Platform
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <ExitToApp fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </Box>
  );
};



export default UserMenu;